import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles, Typography, Button } from '@material-ui/core';
import { Elements, StripeProvider } from 'react-stripe-elements';
import PaymentForm from './PaymentForm';
import VoucherForm from './VoucherForm';
import ContentHeader from './ContentHeader';
import {
    formatTicketType,
    formatDate,
    formatPrice
} from '../helpers/formatters';
import { getCurrencySymbol } from '../helpers/ticket';

const styles = {
    root: {
        textAlign: 'left',
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)'
    },
    description: {
        padding: '10px',
        marginBottom: '20px',
        fontWeight: '100'
    },
    button: {
        height: '40px',
        marginTop: '10px',
        marginBottom: '10px'
    },
    passengerInfo: {
        padding: '10px',
        marginBottom: '20px',
        textAlign: 'center'
    },
    imagePreview: {
        minHeight: '56px',
        margin: '20px auto 0',
        width: '100%',
        maxWidth: '400px',
        display: 'flex',
        justifyContent: 'center',
        '& > img': {
            maxWidth: '100%'
        },
        '@media only screen and (max-device-width: 400px)': {
            margin: '0 auto 0'
        }
    }
};

class ConfirmPurchase extends Component {
    constructor(props) {
        super(props);

        this.state = {
            voucher: null,
            stripe: null
        };
    }

    async componentDidMount() {
        const { client } = this.props;
        const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUB_KEY, {
            stripeAccount: client.stripe_account_id
        });

        this.setState({
            stripe
        });
    }

    setVoucher = voucher => {
        this.setState({ voucher });
    };

    netPrice = price => {
        const { voucher } = this.state;
        if (voucher) {
            return price - voucher.value;
        }
        return price;
    };

    calcEndDate = (type, startDate) => {
        if (type === 'week') {
            const endDate = moment(startDate.toISOString());
            endDate.add(6, 'days').endOf('day');
            return endDate.format('ddd Do MMM YYYY');
        }

        if (type === '4_week') {
            const endDate = moment(startDate.toISOString());
            endDate
                .add(4, 'weeks')
                .subtract(1, 'days')
                .endOf('day');
            return endDate.format('ddd Do MMM YYYY');
        }

        if (type === 'return') {
            const endDate = moment(startDate.toISOString());
            endDate.endOf('day');
            return endDate.format('ddd Do MMM YYYY');
        }

        return 'Unknown';
    };

    render() {
        const {
            handleEdit,
            passenger,
            ticket,
            client,
            service,
            imagePreviewUrl,
            classes,
            buyAgain,
            disablePhoto,
            requireStudentId
        } = this.props;

        const { voucher, stripe } = this.state;

        if (!stripe) {
            return <></>;
        }

        const isFlexiAndNotSingle =
            ticket.type === 'week' ||
            ticket.type === '4_week' ||
            ticket.type === 'return';

        const {
            firstName,
            lastName,
            zone,
            zoneStop,
            stop,
            dest,
            yr,
            startDate,
            startTime,
            id
        } = passenger;

        const useZones =
            ticket.pricing_model === 'Zonal' && ticket.zone_prices && zone;

        return (
            <StripeProvider stripe={stripe}>
                <div>
                    <div className={classes.root}>
                        <ContentHeader
                            text="Ticket details"
                            id="ticket-details-heading"
                        />
                        {!buyAgain && imagePreviewUrl && (
                            <div className={classes.imagePreview}>
                                <img src={imagePreviewUrl} alt="Ticket user" />
                            </div>
                        )}
                        <div className={classes.passengerInfo}>
                            <Typography variant="body2">
                                Passenger: {firstName} {lastName}
                            </Typography>
                            {requireStudentId && id && (
                                <Typography variant="body2">
                                    Student ID: {id}
                                </Typography>
                            )}
                            {stop && (
                                <Typography variant="body2">
                                    Bus stop: {stop}
                                </Typography>
                            )}
                            {zone && (
                                <Typography variant="body2">
                                    Zone: {zone}
                                </Typography>
                            )}
                            {zoneStop && (
                                <Typography variant="body2">
                                    Bus stop: {zoneStop}
                                </Typography>
                            )}
                            {dest && (
                                <Typography variant="body2">
                                    Destination: {dest}
                                </Typography>
                            )}
                            {yr && (
                                <Typography variant="body2">
                                    Year group:{' '}
                                    <span
                                        style={{ textTransform: 'capitalize' }}
                                    >
                                        {yr}
                                    </span>
                                </Typography>
                            )}
                            <Typography variant="body2">
                                Ticket:{' '}
                                {formatTicketType(
                                    service.name,
                                    ticket.type,
                                    ticket.sub_type,
                                    ticket.travel_pattern,
                                    ticket.name
                                )}
                            </Typography>
                            {ticket.start && (
                                <Typography variant="body2">
                                    Valid from: {formatDate(ticket.start)}
                                </Typography>
                            )}
                            {ticket.expiry && (
                                <Typography variant="body2">
                                    Valid until: {formatDate(ticket.expiry)}
                                </Typography>
                            )}
                            {startDate && startTime && (
                                <Typography variant="body2">
                                    Valid: {startDate.format('ddd Do MMM YYYY')}{' '}
                                    - {startTime.toUpperCase()} Only
                                </Typography>
                            )}
                            {startDate && isFlexiAndNotSingle && (
                                <>
                                    <Typography variant="body2">
                                        Valid from:{' '}
                                        {startDate.format('ddd Do MMM YYYY')}{' '}
                                    </Typography>
                                    <Typography variant="body2">
                                        Valid until:{' '}
                                        {this.calcEndDate(
                                            ticket.type,
                                            startDate
                                        )}
                                    </Typography>
                                </>
                            )}
                            <Typography variant="body2">
                                Price:{' '}
                                {formatPrice(
                                    this.netPrice(
                                        useZones
                                            ? ticket.zone_prices[zone]
                                            : ticket.price
                                    ),
                                    ticket.currency
                                )}{' '}
                                {ticket.type === 'flexi' &&
                                    `(${
                                        ticket.default_trips
                                    } trips x ${getCurrencySymbol(
                                        ticket.currency
                                    )}${
                                        useZones
                                            ? ticket.zone_trip_prices[zone]
                                            : ticket.trip_price
                                    })`}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                className={classes.button}
                                onClick={handleEdit}
                            >
                                Edit
                            </Button>
                        </div>
                    </div>
                    <div className={classes.root}>
                        <ContentHeader text="Voucher" id="voucher-heading" />
                        <VoucherForm
                            client={client}
                            setVoucher={this.setVoucher}
                        />
                    </div>
                    <div className={classes.root}>
                        <ContentHeader text="Pay" id="pay-heading" />
                        <Elements
                            fonts={[
                                {
                                    cssSrc:
                                        'https://fonts.googleapis.com/css?family=Roboto:400,500'
                                }
                            ]}
                        >
                            <PaymentForm
                                ticket={ticket}
                                service={service}
                                passenger={passenger}
                                client={client}
                                voucher={voucher}
                                buyAgain={buyAgain}
                                disablePhoto={disablePhoto}
                            />
                        </Elements>
                    </div>
                </div>
            </StripeProvider>
        );
    }
}

ConfirmPurchase.propTypes = {
    handleEdit: PropTypes.func.isRequired,
    passenger: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        photo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        stop: PropTypes.string
    }).isRequired,
    ticket: PropTypes.shape({
        start: PropTypes.string,
        expiry: PropTypes.string,
        price: PropTypes.string
    }).isRequired,
    client: PropTypes.shape().isRequired,
    service: PropTypes.shape().isRequired,
    imagePreviewUrl: PropTypes.string,
    classes: PropTypes.shape().isRequired,
    buyAgain: PropTypes.bool,
    disablePhoto: PropTypes.bool,
    requireStudentId: PropTypes.bool
};

ConfirmPurchase.defaultProps = {
    imagePreviewUrl: '',
    buyAgain: false,
    disablePhoto: false,
    requireStudentId: false
};

export default withStyles(styles)(ConfirmPurchase);
